import { useEffect, useRef } from "react";

/**
 * ReminderEngine — invisible client component.
 * Every 30s while the app is open, checks if it's the user's chosen reminder
 * time and they haven't journaled today. If so, fires a browser notification
 * (when permission granted). The in-app banner handles the same case visually.
 *
 * Keyed in localStorage with `hush_last_notif_<YYYY-MM-DD>` so we never fire twice
 * in the same day.
 */
export default function ReminderEngine({ user, journaledToday }) {
  const firedRef = useRef(false);

  useEffect(() => {
    if (!user?.reminder_enabled) return;
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;

    const check = () => {
      if (journaledToday) return;
      const now = new Date();
      const today = now.toISOString().slice(0, 10);
      const storageKey = `hush_last_notif_${today}`;
      if (firedRef.current || localStorage.getItem(storageKey)) return;

      const [hh, mm] = (user.reminder_time || "20:00").split(":").map(Number);
      const target = new Date(now);
      target.setHours(hh, mm, 0, 0);
      // Fire if current time is within +/- 90s of target
      const diffMs = Math.abs(now.getTime() - target.getTime());
      if (diffMs <= 90 * 1000) {
        try {
          new Notification("A quiet minute with Hush 🌿", {
            body: "Tap to capture how today felt — keep your streak going.",
            icon: "/favicon.ico",
            tag: `hush-daily-${today}`,
          });
          localStorage.setItem(storageKey, String(Date.now()));
          firedRef.current = true;
        } catch (err) {
          console.warn("[ReminderEngine] notification failed:", err);
        }
      }
    };

    check();
    const id = setInterval(check, 30 * 1000);
    return () => clearInterval(id);
  }, [user?.reminder_enabled, user?.reminder_time, journaledToday]);

  return null;
}
